/**
 * AppLink – Unified link component with optional analytics tracking
 *
 * Link types (controlled by `native` prop):
 * - `native={true}` (default): renders a plain <a> for full page refresh (JSP pages)
 * - `native={false}`: uses react-router-dom <Link> for SPA navigation
 * - `target="_blank"`: external link opens in new tab with noopener noreferrer nofollow
 *
 * Analytics tracking (optional):
 * - Pass `eventName` to enable event tracking on click
 * - `eventProperties` for additional event data
 * - Section Name is automatically inferred from nearest [data-analytics-section-name] parent
 */

import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { type LinkProps } from 'react-router-dom';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { AnalyticsEventProviderType } from '@/shared/features/analytics/application/services/AnalyticsService';
import { NavigationLoaderContext } from '@/shared/features/navigation-loader/presentation/context/navigationLoader.context';

export type AppLinkProps = LinkProps & {
  /** When true (default), render a native <a> to force full page refresh */
  shouldRefresh?: boolean;
  /**
   * Optional anchor rel. If not provided and `target="_blank"`, defaults to 'noopener noreferrer nofollow'.
   */
  rel?: string;
  /**
   * Event name to track when link is clicked. If not provided, no tracking occurs.
   */
  eventName?: string;
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>;
  /**
   * Whether to track the event (useful for conditional tracking)
   * @default true
   */
  trackEvent?: boolean;
  /**
   * Which analytics event providers should receive this event
   */
  eventProviders?: AnalyticsEventProviderType[];
  /**
   * When true, show a full-screen navigation loader on click until the next page loads.
   */
  showScreenLoader?: boolean;
  /**
   * When true, show a loader icon after first click and keep it until unmount.
   * Useful for CTA links that trigger navigation or async flows.
   */
  showLoaderOnClick?: boolean;
  /**
   * Optional: explicitly control loading state instead of click-based.
   * If provided, this takes precedence over `showLoaderOnClick`.
   */
  isLoading?: boolean;
  /**
   * When true, link is not clickable (e.g. for submit-style links when form is invalid or loading).
   */
  disabled?: boolean;
};

function toHref(to: LinkProps['to']): string {
  if (typeof to === 'string') return to;
  const pathname = to?.pathname ?? '/';
  const search = to?.search ?? '';
  const hash = to?.hash ?? '';
  return `${pathname}${search}${hash}`;
}

export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
  (props, ref) => {
    const {
      // shouldRefresh = true,
      eventName,
      eventProperties,
      trackEvent = true,
      eventProviders,
      onClick,
      to,
      children,
      showLoaderOnClick,
      showScreenLoader = false,
      isLoading: isLoadingProp,
      disabled = false,
      className,
      ...restProps
    } = props;

    const { analytics, isInitialized } = useAnalytics();
    const navigationLoader = React.useContext(NavigationLoaderContext);
    const [clickedLoading, setClickedLoading] = React.useState(false);

    const useLoader = showLoaderOnClick === true || typeof isLoadingProp === 'boolean';
    const effectiveLoading = typeof isLoadingProp === 'boolean' ? isLoadingProp : !!(showLoaderOnClick && clickedLoading);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      if (showLoaderOnClick) {
        setClickedLoading(true);
      }
      if (showScreenLoader) {
        navigationLoader?.startNavigation();
      }

      // Track event if eventName is provided and tracking is enabled
      if (eventName && trackEvent && analytics && isInitialized) {
        try {
          const url = typeof to === 'string' ? to : JSON.stringify(to);
          const el = e.currentTarget as unknown as HTMLElement;
          const inferredSectionName =
            typeof el?.closest === 'function'
              ? el.closest('[data-analytics-section-name]')?.getAttribute('data-analytics-section-name') ?? undefined
              : undefined;

          const mergedProps: Record<string, unknown> = {
            ...eventProperties,
            ...(eventProperties && 'Section Name' in eventProperties
              ? {}
              : inferredSectionName
                ? { 'Section Name': inferredSectionName }
                : {}),
            'Target URL': url,
          };
          analytics.trackEvent(eventName, mergedProps, { eventProviders });
        } catch (error) {
          console.error(`Error tracking event "${eventName}":`, error);
        }
      }

      // Call original onClick if provided
      onClick?.(e);
    };

    const content = useLoader ? (
      <span className="inline-flex w-full items-center justify-center gap-2">
        {effectiveLoading ? <Loader2 className="animate-spin shrink-0" aria-hidden="true" /> :
        <span className={effectiveLoading ? 'opacity-0' : ''}>{children}</span>}
      </span>
    ) : (
      children
    );

    // if (shouldRefresh) {
      const {
        replace: _replace,
        state: _state,
        reloadDocument: _reloadDocument,
        relative: _relative,
        preventScrollReset: _preventScrollReset,
        unstable_viewTransition: _unstable_viewTransition,
        ...rest
      } = restProps as Omit<LinkProps, 'to' | 'onClick' | 'children'> & {
        unstable_viewTransition?: unknown;
      };

      const href = toHref(to);
      const rel =
        rest.rel ??
        (rest.target === '_blank' ? 'noopener noreferrer nofollow' : undefined);
      const anchorClassName =
        typeof className === 'string' ? className : undefined;
      const mergedClassName = [anchorClassName, disabled && 'pointer-events-none opacity-50'].filter(Boolean).join(' ');

      return (
        <a
          ref={ref}
          href={href}
          rel={rel}
          onClick={handleClick}
          aria-disabled={disabled ? true : undefined}
          className={mergedClassName || undefined}
          {...rest}
        >
          {content}
        </a>
      );
    // }

    // const linkClassName = [className, disabled && 'pointer-events-none opacity-50'].filter(Boolean).join(' ');
    // return (
    //   <Link
    //     ref={ref}
    //     to={to}
    //     onClick={handleClick}
    //     aria-disabled={disabled ? true : undefined}
    //     className={linkClassName || undefined}
    //     {...restProps}
    //   >
    //     {content}
    //   </Link>
    // );
  }
);
AppLink.displayName = 'AppLink';

export type { LinkProps } from 'react-router-dom';
